import qs from 'query-string';

import {
    RETURN_DIRECTION,
    DEPART_DIRECTION,
} from '../../../constants';
import {
    TIMING_DEFAULT,
} from '../constants';
import {
    NEGATIVE_CHAR,
    STICKY_CHAR,
    INCLUDE_ACTION,
    EXCLUDE_ACTION,
    SHORT_TRUTHY_STRING,
    TRUTHY_STRINGS,
    SHORT_FALSY_STRING,
    FALSY_STRINGS,
    RETURN_DIRECTION_KEY,
    DEPART_DIRECTION_KEY,
    DIRECT_TRIPS_ONLY_KEY,
    TIMING_KEY,
    TIMING_DELIMITER,
    FEATURES_KEY,
    CARRIERS_KEY,
    ALLOW_NEARBY_KEY,
    TRAVEL_MODES_KEY,
    SORT_TYPE_KEY,
    STATIONS_KEY,
    STATIONS_DELIMITER,
    PARSED_FILTERS_NOT_TO_COPY,
} from './queryConstants';

const MIN_HOUR = TIMING_DEFAULT[0];
const MAX_HOUR = TIMING_DEFAULT[1];

function _lowercaseQueries(queryParams = {}) {
    return Object.entries(queryParams).reduce((lowered, [key, value = '']) => {
        if (Array.isArray(value)) {
            value = value.pop();
        }

        return {
            ...lowered,
            [key.toLowerCase()]: typeof value === 'string'
                ? value.toLowerCase()
                : value,
        };
    }, {});
}

function _parseQueryParamArray(value) {
    const arr = value.split(',').filter(Boolean);

    return arr.reduce((arr, value) => {
        const firstChar = value[0];
        let action = INCLUDE_ACTION;
        let sticky = false;
        let scope;

        if (firstChar === STICKY_CHAR) {
            sticky = true;
            value = value.slice(1);
        }

        if (firstChar === NEGATIVE_CHAR) {
            action = EXCLUDE_ACTION;
            value = value.slice(1);
        }

        if (value.includes(':')) {
            const parts = value.split(':');
            value = parts[0];
            scope = parts[1];
        }

        return [
            ...arr,
            {
                [value]: {
                    action,
                    sticky,
                    scope,
                },
            },
        ];
    }, []);
}

function _parseBool(value) {
    if (TRUTHY_STRINGS.includes(value)) {
        return true;
    } if (FALSY_STRINGS.includes(value)) {
        return false;
    }

    throw new Error('Error parsing a boolean from query params');
}

function _parseHours(value) {
    const timing = value.split(',');
    if (timing.length !== 1 && timing.length !== 2) {
        throw new Error('The time range query must have at least one and at most two values');
    }

    const hours = timing
        .filter(Boolean)
        .map((num) => parseInt(num, 10));

    const isValid = hours.every((num) => {
        return !isNaN(num) && MIN_HOUR <= num && num <= MAX_HOUR;
    });

    if (!isValid) {
        throw new Error('The time range query hours must be between 0 and 24 inclusively');
    }

    if (hours[1] !== undefined && hours[1] <= hours[0]) {
        throw new Error('The max hour in the time range query must be greater than the min hour');
    }

    return hours;
}

function _writeQueryParamArray(arr) {
    return arr.reduce((arr, queryAction) => {
        let [
            value,
            {
                action,
                sticky,
                scope,
            },
        ] = Object.entries(queryAction)[0];

        if (action === EXCLUDE_ACTION) {
            value = NEGATIVE_CHAR + value;
        }

        if (sticky) {
            value = STICKY_CHAR + value;
        }

        if (scope) {
            value += `:${scope}`;
        }

        return [
            ...arr,
            value,
        ];
    }, []).join(',');
}

function getFilterQueryParams(queryParams, direction) {
    queryParams = typeof queryParams === 'string'
        ? qs.parse(queryParams)
        : queryParams;
    const lowercasedQueries = _lowercaseQueries(queryParams);
    const directions = direction
        ? [direction]
        : [DEPART_DIRECTION, RETURN_DIRECTION];
    return directions.reduce((map, type) => {
        const keys = getFilterKeys(type);
        return {
            ...map,
            [keys.travelModesKey]: lowercasedQueries[keys.travelModesKey],
            [keys.carriersKey]: lowercasedQueries[keys.carriersKey],
            [keys.timingKey]: lowercasedQueries[keys.timingKey],
            [keys.allowNearbyKey]: lowercasedQueries[keys.allowNearbyKey],
            [keys.directTripsOnlyKey]:
                lowercasedQueries[keys.directTripsOnlyKey],
            [keys.featuresKey]: lowercasedQueries[keys.featuresKey],
            [keys.sortTypeKey]: lowercasedQueries[keys.sortTypeKey],
            [keys.stationsKey]: lowercasedQueries[keys.stationsKey],
        };
    }, {});
}

function parseFilterQueryParams(filterQueryParams = {}, direction) {
    const filterKeys = getFilterKeys(direction);

    return {
        travelModes: _parseTravelModesQuery(filterQueryParams[filterKeys.travelModesKey]),
        carriers: _parseCarriersQuery(filterQueryParams[filterKeys.carriersKey]),
        timing: _parseTiming(filterQueryParams[filterKeys.timingKey]),
        allowNearby: _parseAllowNearby(filterQueryParams[filterKeys.allowNearbyKey]),
        directOnly: _parseDirectOnly(filterQueryParams[filterKeys.directTripsOnlyKey]),
        features: _parseFeatures(filterQueryParams[filterKeys.featuresKey]),
        sortType: _parseSortType(filterQueryParams[filterKeys.sortTypeKey]),
        stations: _parseStations(filterQueryParams[filterKeys.stationsKey]),
    };
}

function _parseTravelModesQuery(travelModesQuery) {
    if (!travelModesQuery) {
        return {};
    }

    const parsedQueries = _parseQueryParamArray(travelModesQuery);
    return parsedQueries.reduce((map, queryAction) => {
        const [
            key,
            { action },
        ] = Object.entries(queryAction)[0];
        const travelMode = key.toLowerCase();
        const isActive = action === INCLUDE_ACTION;

        return {
            ...map,
            [travelMode]: isActive,
        };
    }, {});
}

function _parseCarriersQuery(carriersQuery) {
    if (!carriersQuery) {
        return {};
    }

    const parsedQueries = _parseQueryParamArray(carriersQuery);
    return parsedQueries.reduce((map, queryAction) => {
        const [
            key,
            {
                action,
                scope: travelMode = 'all',
            },
        ] = Object.entries(queryAction)[0];
        const carrierCode = key.toUpperCase();
        const isActive = action === INCLUDE_ACTION;

        return {
            ...map,
            [carrierCode]: {
                ...map[carrierCode] || {},
                [travelMode]: isActive,
            },
        };
    }, {});
}

function _parseTiming(timingQuery) {
    if (!timingQuery) {
        return;
    }

    return timingQuery.split(TIMING_DELIMITER).reduce((timing, legTiming) => {
        if (!legTiming) {
            return [
                ...timing,
                undefined,
            ];
        }

        try {
            const parsedTiming = _parseHours(legTiming);
            return [
                ...timing,
                parsedTiming,
            ];
        } catch (err) {
            return [
                ...timing,
                undefined,
            ];
        }
    }, []);
}

function _parseAllowNearby(allowNearbyQuery) {
    if (!allowNearbyQuery) {
        return;
    }

    return _parseBool(allowNearbyQuery);
}

function _parseDirectOnly(allowNearbyQuery) {
    if (!allowNearbyQuery) {
        return;
    }

    return _parseBool(allowNearbyQuery);
}

function _parseFeatures(featuresQuery) {
    if (!featuresQuery) {
        return {};
    }

    const parsedQueries = _parseQueryParamArray(featuresQuery);
    return parsedQueries.reduce((map, queryAction) => {
        const [
            key,
            { action },
        ] = Object.entries(queryAction)[0];
        const featureId = key.toLowerCase();
        const isActive = action === INCLUDE_ACTION;

        return {
            ...map,
            [featureId]: isActive,
        };
    }, {});
}

function _parseStations(stationQuery) {
    if (!stationQuery) return { arrival: {}, departure: {} };
    const [departure, arrival] = stationQuery.split(STATIONS_DELIMITER).map((queryParamArray) => {
        return queryParamArray
            ? _parseQueryParamArray(queryParamArray).reduce((map, queryAction) => {
                const [
                    key,
                    {
                        action,
                    },
                ] = Object.entries(queryAction)[0];
                const stationId = key.toUpperCase();
                const isActive = action === INCLUDE_ACTION;

                return {
                    ...map,
                    [stationId]: isActive,
                };
            }, {})
            : {};
    });
    return {
        arrival,
        departure,
    };
}

function _parseSortType(sortTypeQuery) {
    return sortTypeQuery;
}

function getFilterKeys(direction) {
    let directionKey = null;
    if (direction === RETURN_DIRECTION) {
        directionKey = RETURN_DIRECTION_KEY;
    } else {
        directionKey = DEPART_DIRECTION_KEY;
    }

    return {
        travelModesKey: directionKey + TRAVEL_MODES_KEY,
        carriersKey: directionKey + CARRIERS_KEY,
        timingKey: directionKey + TIMING_KEY,
        allowNearbyKey: directionKey + ALLOW_NEARBY_KEY,
        directTripsOnlyKey: directionKey + DIRECT_TRIPS_ONLY_KEY,
        featuresKey: directionKey + FEATURES_KEY,
        sortTypeKey: directionKey + SORT_TYPE_KEY,
        stationsKey: directionKey + STATIONS_KEY,
    };
}

function writeFilterQueryParams(filterSet, direction) {
    if (!filterSet) {
        return;
    }

    const filterKeys = getFilterKeys(direction);

    return {
        [filterKeys.travelModesKey]: _writeTravelModeQuery(filterSet.travelModes),
        [filterKeys.carriersKey]: _writeCarriersQuery(filterSet.carriers),
        [filterKeys.timingKey]: _writeTimingQuery(filterSet.timing),
        [filterKeys.allowNearbyKey]: _writeAllowNearbyQuery(filterSet.allowNearby),
        [filterKeys.directTripsOnlyKey]: _writeDirectOnlyQuery(filterSet.directOnly),
        [filterKeys.featuresKey]: _writeFeaturesQuery(filterSet.features),
        [filterKeys.sortTypeKey]: _writeSortTypeQuery(filterSet.sortType),
        [filterKeys.stationsKey]: _writeStationsQuery(filterSet.stations),
    };
}

function _writeTravelModeQuery(travelModeMap) {
    if (Object.keys(travelModeMap).length === 0) {
        return;
    }

    const actionified = Object.entries(travelModeMap)
        .reduce((arr, [travelMode, isActive]) => {
            return [
                ...arr,
                {
                    [travelMode]: {
                        action: isActive
                            ? INCLUDE_ACTION
                            : EXCLUDE_ACTION,
                        sticky: false,
                    },
                },
            ];
        }, []);

    return _writeQueryParamArray(actionified);
}

function _writeCarriersQuery(carrierMap) {
    if (Object.keys(carrierMap).length === 0) {
        return;
    }

    const actionified = Object.entries(carrierMap)
        .reduce((arr, [carrierCode, carrierTravelModeMap]) => {
            return Object.entries(carrierTravelModeMap)
                .reduce((arr, [travelMode, isActive]) => {
                    return [
                        ...arr,
                        {
                            [carrierCode.toLowerCase()]: {
                                action: isActive
                                    ? INCLUDE_ACTION
                                    : EXCLUDE_ACTION,
                                scope: travelMode === 'all'
                                    ? undefined
                                    : travelMode,
                                sticky: false,
                            },
                        },
                    ];
                }, arr);
        }, []);

    return _writeQueryParamArray(actionified);
}

function _writeTimingQuery(timing) {
    if (!timing) {
        return;
    }

    return timing
        .map((directionTiming) => {
            if (!directionTiming) {
                return undefined;
            }

            return directionTiming.join(',');
        })
        .join(TIMING_DELIMITER);
}

function _writeAllowNearbyQuery(allowNearby) {
    if (allowNearby === undefined) {
        return;
    }

    return _writeBool(allowNearby);
}

function _writeDirectOnlyQuery(directOnly) {
    if (directOnly === undefined) {
        return;
    }

    return _writeBool(directOnly);
}

function _writeBool(bool) {
    return bool
        ? SHORT_TRUTHY_STRING
        : SHORT_FALSY_STRING;
}

function _writeFeaturesQuery(features = {}) {
    if (Object.keys(features).length === 0) {
        return;
    }

    const actionified = Object.entries(features)
        .reduce((arr, [featureId, isActive]) => {
            return [
                ...arr,
                {
                    [featureId]: {
                        action: isActive
                            ? INCLUDE_ACTION
                            : EXCLUDE_ACTION,
                        sticky: false,
                    },
                },
            ];
        }, []);

    return _writeQueryParamArray(actionified);
}

function _writeSortTypeQuery(sortType) {
    return sortType;
}

function _actionifyStationLeg(stationLeg) {
    return Object.entries(stationLeg).reduce((arr, [id, isActive]) => {
        if (isActive) return arr;
        return arr.concat({
            [id.toLowerCase()]: {
                action: EXCLUDE_ACTION,
                sticky: false,
            },
        });
    }, []);
}

function _writeStationsQuery(stations = {}) {
    const {
        arrival,
        departure,
    } = stations;

    const arriveActionified = _actionifyStationLeg(arrival);
    const departActionified = _actionifyStationLeg(departure);

    if (!!arriveActionified.length || !!departActionified.length) {
        return [
            _writeQueryParamArray(departActionified),
            _writeQueryParamArray(arriveActionified),
        ].join(STATIONS_DELIMITER);
    }
}

function copyFilterQueryParams(sourceFilterParams) {
    const sourceDirection = Object.keys(sourceFilterParams)[0].charAt(0) === DEPART_DIRECTION_KEY
        ? DEPART_DIRECTION
        : RETURN_DIRECTION;

    const targetDirection = sourceDirection === DEPART_DIRECTION
        ? RETURN_DIRECTION
        : DEPART_DIRECTION;

    const parsedSourceFilterParams = parseFilterQueryParams(sourceFilterParams, sourceDirection);

    const parsedFiltersNotToCopyMap = PARSED_FILTERS_NOT_TO_COPY.reduce((map, filter) => {
        return {
            ...map,
            [filter]: undefined,
        };
    }, {});

    const parsedTargetFilterParams = {
        ...parsedSourceFilterParams,
        stations: {
            arrival: parsedSourceFilterParams.stations.departure,
            departure: parsedSourceFilterParams.stations.arrival,
        },
        ...parsedFiltersNotToCopyMap,
    };

    const targetFilterParams = writeFilterQueryParams(parsedTargetFilterParams, targetDirection);

    return targetFilterParams;
}

export {
    copyFilterQueryParams,
    getFilterQueryParams,
    parseFilterQueryParams,
    writeFilterQueryParams,
};
